import { Component, type ReactNode, type ErrorInfo } from 'react';

interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

/**
 * Error Boundary for the Manuscript System.
 * Catches errors in marking/annotation logic and displays a fallback UI
 * while still allowing the passage text to render.
 */
export class ManuscriptErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('[ManuscriptErrorBoundary] Caught error:', error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return this.props.fallback || (
                <div className="fixed bottom-4 right-4 bg-destructive/10 border border-destructive text-destructive px-4 py-2 rounded-md max-w-md">
                    <p className="font-semibold">Manuscript tools temporarily unavailable</p>
                    <p className="text-sm opacity-80">Your annotations are safe. Refresh to restore functionality.</p>
                </div>
            );
        }

        return this.props.children;
    }
}
